'use memo'

import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { searchTeamFinder } from '@sportninja/common/services/teamFinder/teamFinderService'
import { buildSearchBody } from '@sportninja/common/features/teamFinder/buildSearchBody'
import type {
  CompetitionResult,
  TeamFinderFilters,
} from '@sportninja/common/features/teamFinder/types'

interface UseTeamFinderSearchArgs {
  onError: (err: unknown) => void
}

interface UseTeamFinderSearchResult {
  search: (filters: TeamFinderFilters) => void
  loadMore: () => void
  results: CompetitionResult[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  hasSearched: boolean
}

interface SearchVariables {
  filters: TeamFinderFilters
  page: number
}

// Team search is an explicit user action (the "Search" button), so it's modeled
// as a mutation — every click fetches fresh (no caching by design). Paging
// reuses the same mutation with an incremented page and appends the payload,
// so "Show More" grows the list rather than replacing it.
export const useTeamFinderSearch = ({
  onError,
}: UseTeamFinderSearchArgs): UseTeamFinderSearchResult => {
  'use memo'

  const [results, setResults] = useState<CompetitionResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  // The filters the visible results came from. "Show More" must page the
  // *searched* filters, not whatever the user has since typed into the form.
  const searchedFilters = useRef<TeamFinderFilters | null>(null)

  const mutation = useMutation({
    mutationFn: ({ filters, page }: SearchVariables) =>
      searchTeamFinder(buildSearchBody(filters), page),
    onSuccess: (data, variables) => {
      // Page 1 is a fresh search and replaces; later pages append.
      setResults((prev) =>
        variables.page === 1 ? data.results : [...prev, ...data.results]
      )
      setCurrentPage(data.currentPage)
      setTotalPages(data.totalPages)
      setHasSearched(true)
    },
    onError,
  })

  const search = (filters: TeamFinderFilters) => {
    searchedFilters.current = filters
    mutation.mutate({ filters, page: 1 })
  }

  const isLoadingMore =
    mutation.isPending && (mutation.variables?.page ?? 1) > 1

  const hasMore = hasSearched && currentPage < totalPages

  const loadMore = () => {
    if (!searchedFilters.current || !hasMore || mutation.isPending) {
      return
    }
    mutation.mutate({
      filters: searchedFilters.current,
      page: currentPage + 1,
    })
  }

  return {
    search,
    loadMore,
    results,
    // The skeleton is for a cold search only — paging keeps the existing
    // results on screen and shows its own busy state on the button.
    isLoading: mutation.isPending && !isLoadingMore,
    isLoadingMore,
    hasMore,
    hasSearched,
  }
}
